import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { LeadSearchForm } from '../components/LeadSearchForm';
import { LeadSearchResults } from '../components/LeadSearchResults';
import { searchLeads } from '../api/searchLeads';
import { useReferenceData } from '../hooks/useReferenceData';
import type {
  LeadSearchFilters,
  LeadSearchResult,
  LeadSearchResultItem,
} from '../types/leadSearch';
import type { LeadStatus, DamageCategory } from '../types';

const DEFAULT_PAGE_SIZE = 25;

export function LeadSearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState<LeadSearchFilters>({});
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [results, setResults] = useState<LeadSearchResult<LeadSearchResultItem> | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { users, insuranceCompanies } = useReferenceData();

  // Load filters from URL on mount
  useEffect(() => {
    const filtersFromUrl: LeadSearchFilters = {};

    // Customer Info
    if (searchParams.get('firstName')) filtersFromUrl.firstName = searchParams.get('firstName')!;
    if (searchParams.get('lastName')) filtersFromUrl.lastName = searchParams.get('lastName')!;
    if (searchParams.get('companyName'))
      filtersFromUrl.companyName = searchParams.get('companyName')!;
    if (searchParams.get('phone')) filtersFromUrl.phone = searchParams.get('phone')!;
    if (searchParams.get('email')) filtersFromUrl.email = searchParams.get('email')!;

    // Property Address
    if (searchParams.get('propertyStreet'))
      filtersFromUrl.propertyStreet = searchParams.get('propertyStreet')!;
    if (searchParams.get('propertyCity'))
      filtersFromUrl.propertyCity = searchParams.get('propertyCity')!;
    if (searchParams.get('propertyState'))
      filtersFromUrl.propertyState = searchParams.get('propertyState')!;
    if (searchParams.get('propertyZip'))
      filtersFromUrl.propertyZip = searchParams.get('propertyZip')!;

    // Billing Address
    if (searchParams.get('billingStreet'))
      filtersFromUrl.billingStreet = searchParams.get('billingStreet')!;
    if (searchParams.get('billingCity'))
      filtersFromUrl.billingCity = searchParams.get('billingCity')!;
    if (searchParams.get('billingState'))
      filtersFromUrl.billingState = searchParams.get('billingState')!;
    if (searchParams.get('billingZip'))
      filtersFromUrl.billingZip = searchParams.get('billingZip')!;

    // Job Info
    if (searchParams.get('jobNumber')) filtersFromUrl.jobNumber = searchParams.get('jobNumber')!;
    if (searchParams.get('status')) filtersFromUrl.status = searchParams.get('status') as LeadStatus;
    if (searchParams.get('damageType'))
      filtersFromUrl.damageType = searchParams.get('damageType') as DamageCategory;

    // Dates
    if (searchParams.get('leadDateFrom'))
      filtersFromUrl.leadDateFrom = searchParams.get('leadDateFrom')!;
    if (searchParams.get('leadDateTo'))
      filtersFromUrl.leadDateTo = searchParams.get('leadDateTo')!;
    if (searchParams.get('openedDateFrom'))
      filtersFromUrl.openedDateFrom = searchParams.get('openedDateFrom')!;
    if (searchParams.get('openedDateTo'))
      filtersFromUrl.openedDateTo = searchParams.get('openedDateTo')!;
    if (searchParams.get('closedDateFrom'))
      filtersFromUrl.closedDateFrom = searchParams.get('closedDateFrom')!;
    if (searchParams.get('closedDateTo'))
      filtersFromUrl.closedDateTo = searchParams.get('closedDateTo')!;

    // Assignment
    if (searchParams.get('assignedToUserId'))
      filtersFromUrl.assignedToUserId = Number(searchParams.get('assignedToUserId'));
    if (searchParams.get('insuranceCompanyId'))
      filtersFromUrl.insuranceCompanyId = Number(searchParams.get('insuranceCompanyId'));

    // Pagination
    const pageFromUrl = searchParams.get('page');
    const pageSizeFromUrl = searchParams.get('pageSize');

    setFilters(filtersFromUrl);
    if (pageSizeFromUrl) setPageSize(Number(pageSizeFromUrl));

    // Auto-search if there are filters in URL
    if (Object.keys(filtersFromUrl).length > 0) {
      performSearch(
        filtersFromUrl,
        pageFromUrl ? Number(pageFromUrl) : 1,
        pageSizeFromUrl ? Number(pageSizeFromUrl) : DEFAULT_PAGE_SIZE
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update URL when filters/pagination change
  const updateUrl = useCallback(
    (newFilters: LeadSearchFilters, newPage: number, newPageSize: number) => {
      const params = new URLSearchParams();

      // Add filters to URL
      Object.entries(newFilters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.set(key, value.toString());
        }
      });

      // Add pagination
      params.set('page', newPage.toString());
      params.set('pageSize', newPageSize.toString());

      setSearchParams(params);
    },
    [setSearchParams]
  );

  const performSearch = async (
    searchFilters: LeadSearchFilters,
    searchPage: number,
    searchPageSize: number
  ) => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await searchLeads({
        ...searchFilters,
        page: searchPage,
        pageSize: searchPageSize,
      });
      setResults(result);
      updateUrl(searchFilters, searchPage, searchPageSize);
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } } };
      setError(error.response?.data?.message || 'Failed to search leads');
      setResults(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = () => {
    performSearch(filters, 1, pageSize);
  };

  const handleClear = () => {
    setFilters({});
    setPageSize(DEFAULT_PAGE_SIZE);
    setResults(null);
    setSearchParams(new URLSearchParams());
  };

  const handlePageChange = (newPage: number) => {
    performSearch(filters, newPage, pageSize);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    performSearch(filters, 1, newPageSize);
  };

  return (
    <div className="min-h-screen bg-[#f8f8f8] px-5 py-6 space-y-6">
      {/* Header */}
      <div className="bg-white rounded-[20px] px-5 py-6">
        <h2 className="text-2xl font-semibold text-[#0a0a9e]">Lead Search</h2>
        <p className="text-[#84878A] mt-2">
          Search and filter leads with comprehensive criteria
        </p>
      </div>

      {/* Search Form */}
      <LeadSearchForm
        filters={filters}
        onFiltersChange={setFilters}
        onSearch={handleSearch}
        onClear={handleClear}
        users={users}
        insuranceCompanies={insuranceCompanies}
      />

      <ErrorAlert error={error} className="rounded-[20px] p-4" />

      {/* Search Results */}
      <LeadSearchResults
        results={results}
        isLoading={isLoading}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />
    </div>
  );
}
